import { FileJson, Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { exportAssetProvenance } from "../api";
import { useAssetProvenance } from "../hooks";
import { cn } from "~/shared/lib/utils";
import { Badge } from "~/shared/ui/badge";
import { Button } from "~/shared/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "~/shared/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/shared/ui/tabs";

interface AssetProvenanceDialogProps {
    assetId: string;
    workspaceId?: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

function jsonText(value: unknown): string {
    try {
        return JSON.stringify(value, null, 2) ?? String(value);
    } catch {
        return String(value);
    }
}

function Value({ value }: { value: unknown }) {
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return <span>{String(value)}</span>;
    }
    return <code className="text-xs break-all whitespace-pre-wrap">{jsonText(value)}</code>;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section className="space-y-2">
            <h3 className="text-sm font-semibold">{title}</h3>
            {children}
        </section>
    );
}

function JsonList({ items }: { items: readonly unknown[] }) {
    if (items.length === 0) return <p className="text-sm text-muted-foreground">—</p>;
    return (
        <div className="space-y-2">
            {items.map((item, index) => (
                <pre
                    key={index}
                    className="overflow-x-auto rounded-md bg-muted p-3 text-xs whitespace-pre-wrap"
                >
                    {jsonText(item)}
                </pre>
            ))}
        </div>
    );
}

function JsonBlock({ value }: { value: unknown }) {
    return value == null ? (
        <p className="text-sm text-muted-foreground">—</p>
    ) : (
        <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs whitespace-pre-wrap">
            {jsonText(value)}
        </pre>
    );
}

function downloadReason(
    kind: "pending" | "failed" | "quarantined" | "unavailable",
    t: (key: string) => string,
): string {
    return t(`provenance.downloadDisabled.${kind}`);
}

interface DownloadActionProps {
    label: string;
    icon: ReactNode;
    disabled: boolean;
    disabledReason?: string;
    onClick: () => void;
}

function DownloadAction({ label, icon, disabled, disabledReason, onClick }: DownloadActionProps) {
    return (
        <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            title={disabled ? disabledReason : label}
            onClick={onClick}
        >
            {icon}
            {label}
        </Button>
    );
}

export function AssetProvenanceDialog({
    assetId,
    workspaceId,
    open,
    onOpenChange,
}: AssetProvenanceDialogProps) {
    const { t } = useTranslation("assets");
    const query = useAssetProvenance(assetId, workspaceId, { enabled: open });
    const provenance = query.data;
    const kind = provenance?.status ?? "unavailable";
    const isReady = kind === "ready";
    const downloadDisabledReason = !isReady ? downloadReason(kind, t) : undefined;

    const download = (filename: string) => {
        const promise = exportAssetProvenance(assetId, filename, workspaceId);
        void promise.catch((error: unknown) => console.error("provenance download failed", error));
    };

    const rawValue = provenance?.rawJson;
    const parameters: Record<string, unknown> = provenance
        ? {
              prompt: provenance.prompt,
              instruction: provenance.instruction,
              model: provenance.model,
              settings_json: provenance.settingsJson,
          }
        : {};
    const references = provenance?.references ?? [];
    const lineage = provenance?.lineage;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{t("provenance.title")}</DialogTitle>
                    <DialogDescription>{t("provenance.description", { id: assetId })}</DialogDescription>
                </DialogHeader>

                {query.isLoading ? (
                    <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
                        <Loader2 className="size-4 animate-spin" aria-hidden />
                        {t("provenance.loading")}
                    </div>
                ) : query.isError || !provenance ? (
                    <p className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm">
                        {t("provenance.error")}
                    </p>
                ) : (
                    <div className="space-y-6">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm text-muted-foreground">{t("provenance.status")}</span>
                            <Badge
                                variant={
                                    kind === "ready"
                                        ? "success"
                                        : kind === "failed"
                                          ? "destructive"
                                          : "warning"
                                }
                            >
                                {t(`provenance.statuses.${kind}`)}
                            </Badge>
                            {!isReady && (
                                <span className="text-xs text-muted-foreground">
                                    {t(`provenance.statusHelp.${kind}`)}
                                </span>
                            )}
                        </div>

                        <Section title={t("provenance.summary")}>
                            <JsonBlock
                                value={{
                                    origin: provenance.origin,
                                    platform: provenance.platform,
                                    media_type: provenance.mediaType,
                                    mime_type: provenance.mimeType,
                                    occurred_at: provenance.occurredAt,
                                    created_at: provenance.createdAt,
                                    sha256: provenance.sha256,
                                    bytes: provenance.bytes,
                                    source_url: provenance.sourceUrl,
                                    storage_url: provenance.storageUrl,
                                }}
                            />
                        </Section>

                        <Section title={t("provenance.parameters")}>
                            {Object.keys(parameters).length === 0 ? (
                                <p className="text-sm text-muted-foreground">—</p>
                            ) : (
                                <dl className="grid gap-2 sm:grid-cols-2">
                                    {Object.entries(parameters).map(([key, value]) => (
                                        <div key={key} className="rounded-md border p-2 text-sm">
                                            <dt className="text-xs text-muted-foreground">{key}</dt>
                                            <dd className="mt-1 break-words">
                                                <Value value={value} />
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            )}
                        </Section>

                        <div className="grid gap-6 md:grid-cols-2">
                            <Section title={t("provenance.references")}>
                                <JsonList items={references} />
                            </Section>
                            <Section title={t("provenance.lineage")}>
                                <JsonBlock value={lineage} />
                            </Section>
                        </div>

                        <Section title={t("provenance.c2pa.title")}>
                            <div className="rounded-md border p-3 text-sm">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span>{t("provenance.c2pa.status")}</span>
                                    <Badge variant={provenance.existingC2pa ? "info" : "outline"}>
                                        {provenance.existingC2pa
                                            ? provenance.manifestStatus
                                            : t("provenance.c2pa.none")}
                                    </Badge>
                                </div>
                                <dl className="mt-3 space-y-1 text-xs">
                                    <div className="flex gap-2">
                                        <dt className="font-medium">manifest_status</dt>
                                        <dd>{provenance.manifestStatus}</dd>
                                    </div>
                                    {provenance.manifestPointer ? (
                                        <div className="flex gap-2">
                                            <dt className="font-medium">manifest_pointer</dt>
                                            <dd className="break-all">{provenance.manifestPointer}</dd>
                                        </div>
                                    ) : null}
                                </dl>
                            </div>
                        </Section>

                        <Section title={t("provenance.downloads.title")}>
                            <div className="flex flex-wrap gap-2">
                                <DownloadAction
                                    label={t("provenance.downloads.normalized")}
                                    icon={<FileJson className="size-4" aria-hidden />}
                                    disabled={!isReady}
                                    disabledReason={downloadDisabledReason}
                                    onClick={() => download(`asset-${assetId}-provenance.json`)}
                                />
                            </div>
                        </Section>

                        <Tabs defaultValue="raw" className="min-w-0">
                            <TabsList>
                                <TabsTrigger value="raw">{t("provenance.rawJson")}</TabsTrigger>
                                <TabsTrigger value="details">{t("provenance.details")}</TabsTrigger>
                            </TabsList>
                            <TabsContent value="raw" className="mt-2">
                                {rawValue === null || rawValue === undefined ? (
                                    <p className="rounded-md border p-3 text-sm text-muted-foreground">
                                        {t("provenance.rawUnavailable")}
                                    </p>
                                ) : (
                                    <pre
                                        className={cn(
                                            "max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs",
                                            !isReady && "opacity-70",
                                        )}
                                    >
                                        {jsonText(rawValue)}
                                    </pre>
                                )}
                            </TabsContent>
                            <TabsContent value="details" className="mt-2 text-sm text-muted-foreground">
                                {t("provenance.detailsHelp")}
                            </TabsContent>
                        </Tabs>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
